/* Modulo: Slideshow Index — slide engine. Prefix: sli.

   The index is the only navigation: there are no arrow buttons, because every
   slide is already one click away and a second control for the same job is
   what makes a hero feel cluttered. Keyboard left/right still work on the
   stage, which is what the stage's tabindex and aria-label are for.

   Autoplay is OFF by default and deliberately carries NO progress indicator —
   `slh` owns ring-as-timer and `slc` fills its bar during autoplay. This file
   owns wayfinding and cedes timing, or the four slideshows converge.

   `moduloLazyInit` is deliberately NOT used: it does not exist on disk, and 61
   files in this repo carry a dead branch calling it.
*/
(function () {
  'use strict';
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  /* prefers-reduced-motion must be read LIVE, never captured as a boolean at
     load — a visitor toggling it mid-session would otherwise keep an autoplay
     they have just asked to stop (the `as` lesson). */
  var motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reduceMotion() { return !!(motionQuery && motionQuery.matches); }

  function initSection(root) {
    if (!root || root.moduloSli) return;

    var stage   = root.querySelector('[data-sli-stage]');
    var slides  = [].slice.call(root.querySelectorAll('[data-sli-slide]'));
    var entries = [].slice.call(root.querySelectorAll('[data-sli-go]'));
    var live    = root.querySelector('[data-sli-live]');
    if (!stage || slides.length < 2) return;

    var total    = slides.length;
    var current  = 0;
    var timer    = null;
    var hovering = false;
    var focused  = false;
    var onScreen = true;

    var autoplay = root.getAttribute('data-autoplay') === 'true';
    var interval = parseInt(root.getAttribute('data-interval'), 10);
    if (!interval || interval < 1) interval = 6;

    function announce(i) {
      if (!live) return;
      var label = entries[i] ? entries[i].getAttribute('aria-label') : '';
      if (!label) {
        var span = entries[i] ? entries[i].querySelector('.modulo-sli__label') : null;
        label = span ? span.textContent : '';
      }
      /* textContent, never innerHTML — the label is merchant-typed */
      live.textContent = 'Slide ' + (i + 1) + ' of ' + total + (label ? ': ' + label : '');
    }

    function show(i) {
      if (i === current || i < 0 || i >= total) return;
      slides[current].classList.remove('is-active');
      if (entries[current]) {
        entries[current].classList.remove('is-active');
        entries[current].removeAttribute('aria-current');
      }
      current = i;
      slides[current].classList.add('is-active');
      if (entries[current]) {
        entries[current].classList.add('is-active');
        entries[current].setAttribute('aria-current', 'true');
      }
      announce(current);
    }

    function next() { show((current + 1) % total); }
    function prev() { show((current - 1 + total) % total); }

    /* ── autoplay ──────────────────────────────────────────────────────────
       Paused whenever the visitor is reading it (hover), driving it (focus),
       looking elsewhere (tab hidden, scrolled off screen), or has asked for
       reduced motion. Re-checked on every tick rather than latched, so a
       reduced-motion toggle mid-session takes effect immediately. */
    function canPlay() {
      return autoplay && !hovering && !focused && onScreen &&
             !document.hidden && !reduceMotion();
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function start() {
      stop();
      if (!canPlay()) return;
      timer = setInterval(function () {
        if (!canPlay()) { stop(); return; }
        next();
      }, interval * 1000);
    }

    entries.forEach(function (btn) {
      btn.addEventListener('click', function () {
        show(parseInt(btn.getAttribute('data-sli-go'), 10) || 0);
        start();
      });
    });

    stage.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); next(); start(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); start(); }
    });

    if (autoplay) {
      root.addEventListener('pointerenter', function () { hovering = true; stop(); });
      root.addEventListener('pointerleave', function () { hovering = false; start(); });
      root.addEventListener('focusin',  function () { focused = true; stop(); });
      root.addEventListener('focusout', function () { focused = false; start(); });

      if (typeof IntersectionObserver === 'function') {
        new IntersectionObserver(function (obs) {
          for (var i = 0; i < obs.length; i++) {
            onScreen = obs[i].isIntersecting;
            if (onScreen) start(); else stop();
          }
        }, { threshold: 0.15 }).observe(root);
      }
      if (motionQuery) {
        var onMotion = function () { if (reduceMotion()) stop(); else start(); };
        if (motionQuery.addEventListener) motionQuery.addEventListener('change', onMotion);
        else if (motionQuery.addListener) motionQuery.addListener(onMotion);
      }
      start();
    }

    root.moduloSli = {
      resume: start,
      pause: stop,
      destroy: function () {
        stop();
        root.moduloSli = null;
      }
    };
  }

  function bootstrap(scope) {
    var els = (scope || document).querySelectorAll('[data-modulo-sli]');
    for (var i = 0; i < els.length; i++) initSection(els[i]);
  }

  /* An identical <script src> still EXECUTES once per tag, so two installs of
     this section on one page would otherwise wire the document listeners twice. */
  if (!window.moduloSliWired) {
    window.moduloSliWired = true;

    /* a hidden tab throttles timers unpredictably, so the interval is torn
       down on leave and re-armed on return rather than left to drift */
    document.addEventListener('visibilitychange', function () {
      var els = document.querySelectorAll('[data-modulo-sli]');
      for (var i = 0; i < els.length; i++) {
        var api = els[i].moduloSli;
        if (!api) continue;
        if (document.hidden) api.pause(); else api.resume();
      }
    });

    /* theme editor: re-init the section that was just re-rendered */
    document.addEventListener('shopify:section:load', function (e) {
      bootstrap(e.target);
    });
    document.addEventListener('shopify:section:unload', function (e) {
      var els = e.target.querySelectorAll('[data-modulo-sli]');
      for (var i = 0; i < els.length; i++) {
        if (els[i].moduloSli) els[i].moduloSli.destroy();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bootstrap(); });
  } else {
    bootstrap();
  }
})();
